<template>
  <ToolShell
    class="margin-calculator"
    tool-name="Profit margin calculator"
    h1="Profit margin calculator for service businesses."
    lede="Enter the job price, direct costs, and allocated overhead to estimate job profit or loss, margin, and markup. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Margin and markup']"
    form-title="Your numbers"
    :upsell-points="[
      'Margin tracked per client and per project',
      'Reporting built in — no re-running numbers',
      'Quotes, invoices, and payments stay connected',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'Estimated job profit or loss, margin, and markup from your entered costs',
      'No history across clients or projects',
      'You re-run the numbers manually every time',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Margin tracked per client and per project with reporting',
      'Expenses and time stay attached to the job',
      'Quotes, invoices, and payment links in one place',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/profit-margin-calculator"
    :meta-description="metaDescription"
    doc-brand="Profit margin calculator"
    doc-subtitle="Job estimate from your entered costs"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Job price ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.price" type="number" min="0" step="0.01" placeholder="2500" />
        </div>
      </label>

      <label class="ta-field">
        <span>Labor cost ($, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.labor" type="number" min="0" step="0.01" placeholder="900" />
        </div>
      </label>

      <label class="ta-field">
        <span>Materials / other direct costs ($, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 6.5 8 3l5.5 3.5v5L8 15l-5.5-3.5v-5zM8 7.5V15M2.5 6.5 8 10l5.5-3.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.number="form.materials" type="number" min="0" step="0.01" placeholder="400" />
        </div>
      </label>

      <label class="ta-field">
        <span>Overhead allocation ($, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.number="form.overhead" type="number" min="0" step="0.01" placeholder="200" />
        </div>
      </label>
      <p class="margin-help">Enter a non-negative job price. Blank optional costs count as zero; add every cost you want included. Use the same currency throughout.</p>
      <p v-if="!result" class="margin-help" role="status">Enter a job price and valid non-negative costs to see a result.</p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Job price</span><strong>{{ money(result?.price) }}</strong></div>
        <div><span>Total costs</span><strong>{{ money(result?.costs) }}</strong></div>
        <div><span>Margin</span><strong>{{ percent(result?.margin) }}</strong></div>
        <div><span>Markup</span><strong>{{ percent(result?.markup) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Cost breakdown</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Labor</td>
              <td class="ta-num">{{ money(form.labor === null || form.labor === "" ? 0 : form.labor) }}</td>
            </tr>
            <tr>
              <td>Materials / other direct costs</td>
              <td class="ta-num">{{ money(form.materials === null || form.materials === "" ? 0 : form.materials) }}</td>
            </tr>
            <tr>
              <td>Overhead allocation</td>
              <td class="ta-num">{{ money(form.overhead === null || form.overhead === "" ? 0 : form.overhead) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>Estimated job profit or loss</span>
        <strong>{{ money(result?.profit) }}</strong>
      </div>

      <p class="ta-doc-note">Profit or loss = job price − labor − materials and other direct costs − allocated overhead. Margin divides that result by job price; markup divides it by the total entered costs. Both percentages multiply the ratio by 100. A dash means the result is unavailable, including a percentage with a zero denominator.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the job price</h2><p>What the client pays for the finished work.</p></div></div>
      <div><span>02</span><div><h2>Add costs and allocated overhead</h2><p>Include labor and other direct costs, then your allocation of shared overhead. Avoid counting the same expense twice.</p></div></div>
      <div><span>03</span><div><h2>Read profit or loss, margin, markup</h2><p>Negative results show a loss. This estimate covers only the costs you enter; it does not establish after-tax net profit or cash collected.</p></div></div>
    </template>
    <section class="margin-context" aria-labelledby="margin-example">
      <h2 id="margin-example">How do margin and markup differ?</h2>
      <p>For an illustrative $2,500 job with $900 labor, $400 other direct costs, and $200 allocated overhead, total entered costs are $1,500. Estimated job profit is $1,000, margin is 40%, and markup is 66.7%. These are example inputs, not market benchmarks or recommended targets.</p>
      <p>If a $100 job costs $125, the result is a $25 loss, a −25% margin, and a −20% markup.</p>
      <h2>Which benchmark can you compare with this result?</h2>
      <p>This tool includes your overhead allocation, so its result is not labeled gross profit. It is not after-tax net profit either: taxes, financing, and any omitted expenses remain outside the estimate. Compare only figures with matching cost definitions and reporting periods.</p>
      <p><a href="https://quickbooks.intuit.com/my/r/running-a-business/calculate-gross-profit-margin-formula/" target="_blank" rel="noopener noreferrer">Intuit explains gross, operating, and net margin definitions.</a> For source periods, agency populations, and limitations, read the <NuxtLink :to="localePath('/research/agency-profit-margin-benchmarks')">agency profit margin evidence review</NuxtLink>.</p>
    </section>
  </ToolShell>
</template>

<script setup>
import ToolShell from "~/components/tools/ToolShell.vue";
import { calculateJobMargin } from "~/utils/job-margin.mjs";

const { localePath } = useWorkspaceLocale();

const form = reactive({ price: null, labor: null, materials: null, overhead: null });

const result = computed(() => calculateJobMargin(form));
const money = (value) => value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
  ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value));
const percent = (value) => value === null || value === undefined ? "—" : `${value.toFixed(1)}%`;

const metaDescription =
  "Free profit margin calculator: enter job price, direct costs, and allocated overhead to estimate profit or loss, margin, and markup. No signup.";

useHead(() => ({
  title: "Free Profit Margin Calculator for Services | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Profit Margin Calculator | Workspace369" },
    {
      name: "twitter:description",
      content: "Estimate job profit or loss, margin, and markup from job price, direct costs, and allocated overhead. Free, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Profit Margin Calculator | Workspace369" },
    {
      property: "og:description",
      content: "Estimate job profit or loss, margin, and markup from job price, direct costs, and allocated overhead. Free, no signup.",
    },
  ],
}));
</script>

<style scoped>
.margin-calculator :deep(.ta-grid > *) { min-width: 0; }
.margin-calculator :deep(.ta-doc-letterhead) { flex-wrap: wrap; }
.margin-calculator :deep(.ta-doc-brandtext) { flex: 1 1 10rem; }
.margin-calculator :deep(.ta-doc-brandtext strong),
.margin-calculator :deep(.ta-doc-brandtext span) { white-space: normal; overflow-wrap: anywhere; }
.margin-calculator :deep(.ta-doc-stamp) { display: none; }
.margin-calculator :deep(.ta-doc-total) { flex-wrap: wrap; }
.margin-calculator :deep(.ta-doc-total span) { flex: 1 1 10rem; }
.margin-calculator :deep(.ta-doc-total strong) { flex: 0 0 auto; max-width: 100%; overflow-wrap: anywhere; }
.margin-help { color: #475569; font-size: 0.875rem; line-height: 1.6; margin-top: 1rem; }
.margin-context { max-width: 72rem; margin: 0 auto; padding: 3rem 1.25rem; color: #334155; }
.margin-context h2 { font-size: 1.5rem; font-weight: 600; margin-bottom: 1rem; }
.margin-context p { max-width: 70ch; line-height: 1.75; margin-bottom: 1.25rem; }
.margin-context a { text-decoration: underline; text-underline-offset: 0.2em; }

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .margin-help {
  color: #cbd5e1;
}

.dark .margin-context {
  color: #f1f5f9;
}


</style>
